import React, { useEffect, useRef, useState } from 'react';
import config from '../config';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  CssBaseline,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Switch,
  Tooltip,
  Divider,
  Select,
  Paper,
  Collapse,
  ClickAwayListener,
  Popper,
  BottomNavigation,
  BottomNavigationAction,
  ButtonBase,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Straighten as UnitsIcon,
  Thermostat as MaturityIcon,
  UploadFile as BulkImportIcon,
  TuneRounded as TargetsIcon,
  WaterDrop as EvaporationIcon,
  Air as EnvironmentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  NotificationsNone as NotificationBellIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ExitToApp as ExitToAppIcon,
  Map as MapIcon,
  Memory as SimulatorIcon,
} from '@mui/icons-material';
import {
  AdminSlidersIcon,
  BatchingPlantIcon,
  ClientsCardIcon,
  ControlPanelIcon,
  HardwareNodesIcon,
  LimitsChartIcon,
  MixerTruckIcon,
  ProjectsBuildingsIcon,
  SettingsCogIcon,
  TrainingCapIcon,
  UsersGroupIcon,
} from './icons/NavIcons';
import { useAuth } from '../context/AuthContext';
import { useMapView } from '../context/MapViewContext';
import { usePourNav } from '../context/PourNavContext';
import { useNotifications } from '../context/NotificationsContext';
import NotificationsPanel, { UpdateAvailableBanner } from './NotificationsPanel';
import { getVisibleMenuKeys, type MenuKey } from '../utils/userPermissions';
import { useIsMobile } from '../utils/useIsMobile';
import ErrorBoundary from './ErrorBoundary';
import CompanyBrand from './CompanyBrand';
import MobileHeaderBar from './mobile/MobileHeaderBar';
import { MOBILE_APPBAR_HEIGHT, SAFE_AREA_BOTTOM, aboveBottomBar } from './mobile/mobileChrome';
import { sectionCrumbs, sectionFromSearch } from './maturity/maturitySections';

// Same width in both the new and classic looks. The longest labels
// ("Concrete Strength Targets") ellipsize at this width — hovering a
// truncated item shows the full name via the native title tooltip.
const drawerWidthOpen = 210;
// 72px (MUI mini-drawer standard) keeps the icon column at the same x-position
// in both states, so icons don't drift sideways while the width animates.
const drawerWidthClosed = 72;

// Each route wraps its own <DashboardLayout>. React Router usually keeps the
// same instance across navigations, but a route with another wrapper (such as
// RoleProtectedRoute) mounts a new one. Transient sidebar UI state lives at
// module scope so expanding the sidebar or a group survives page changes
// either way (resets on full page reload).
let sidebarOpenCache: boolean | null = null;
let openGroupsCache: Record<string, boolean> = {};

// Sidebar nav can be a single link (leaf) or a collapsible group of links.
type NavLeaf = { kind: 'leaf'; key: MenuKey; text: string; icon: React.ReactNode; path: string };
type NavGroup = { kind: 'group'; id: string; text: string; icon: React.ReactNode; children: NavLeaf[] };
type NavEntry = NavLeaf | NavGroup;

const leaf = (key: MenuKey, text: string, icon: React.ReactNode, path: string): NavLeaf =>
  ({ kind: 'leaf', key, text, icon, path });

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpenState] = useState<boolean>(() => sidebarOpenCache ?? false);
  const setSidebarOpen = (open: boolean) => {
    sidebarOpenCache = open;
    setSidebarOpenState(open);
  };
  // "Classic menu" mini switch — restores the pre-28.07 sidebar look
  // (plain icons, highlighted-row selection with green left border, old
  // width/spacing) while keeping the new menu structure. Persisted per
  // browser so it survives reloads.
  const [classicSidebar, setClassicSidebarState] = useState<boolean>(
    () => localStorage.getItem('sidebarClassic') === '1',
  );
  const setClassicSidebar = (classic: boolean) => {
    localStorage.setItem('sidebarClassic', classic ? '1' : '0');
    setClassicSidebarState(classic);
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Per-group open/closed override for the collapsible sidebar sections. When a
  // group id isn't present here, it defaults to open if it contains the active route.
  const [openGroups, setOpenGroupsState] = useState<Record<string, boolean>>(() => openGroupsCache);
  const setOpenGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>> = (action) => {
    setOpenGroupsState((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      openGroupsCache = next;
      return next;
    });
  };

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // 'POP' | 'PUSH' | 'REPLACE' (the router's Action enum, read as text)
  const navigationType: string = useNavigationType();
  const { t, i18n } = useTranslation();
  const mapView = useMapView();
  const { nav: pourNav, title: pourTitle } = usePourNav();
  // Phone and tablet layout (below md): slim AppBar from the page's
  // published header, hamburger drawer and bottom bar.
  const isMobileLayout = useIsMobile();

  const isPourDashboard = location.pathname.endsWith('/dashboard') && location.pathname.startsWith('/pours/');

  React.useEffect(() => {
    if (isPourDashboard) {
      setSidebarOpen(false);
    }
  }, [isPourDashboard]);

  // Phone layout: each new page opens at the top. The layout instance is
  // usually reused across routes, so this keys on the history entry rather
  // than on mount. Back / Forward (POP) keep the browser's own scroll
  // restore, and a page that only replaces its own URL (a tab or filter in
  // the query string) keeps its place.
  const lastPathRef = React.useRef<string | null>(null);
  React.useLayoutEffect(() => {
    const samePath = lastPathRef.current === location.pathname;
    lastPathRef.current = location.pathname;
    if (!isMobileLayout || navigationType === 'POP') return;
    if (navigationType === 'REPLACE' && samePath) return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    i18n.changeLanguage(event.target.value);
  };

  const drawerWidth = sidebarOpen ? drawerWidthOpen : drawerWidthClosed;

  // The phone bar grows when a page's title wraps (wrapTitle): the space
  // under it follows the bar's real height so nothing hides behind it.
  const mobileBarRef = useRef<HTMLDivElement | null>(null);
  const [mobileBarHeight, setMobileBarHeight] = useState(MOBILE_APPBAR_HEIGHT);
  useEffect(() => {
    const el = mobileBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setMobileBarHeight(Math.max(MOBILE_APPBAR_HEIGHT, Math.round(el.getBoundingClientRect().height)));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobileLayout]);

  // Routes that render a map the bottom-nav Map button can open/close in place.
  const MAP_ROUTES = ['/', '/pours', '/projects', '/hardware', '/batching-plants'];
  const isMapRoute = MAP_ROUTES.includes(location.pathname);

  // Mobile bottom-nav highlight. The Map slot lights up while the map is open
  // on any map route; Control Panel on both of its paths; "Account" opens a
  // menu so it owns no route.
  const bottomNavValue =
    isMapRoute && mapView.open ? 'map'
      : location.pathname === '/' || location.pathname === '/pours' ? 'pours'
        : false;

  // Material 3 style: a soft green pill behind the selected tab's icon. Every
  // icon has the same padding, so nothing shifts when the selection moves.
  const bottomNavSx = {
    '& .MuiBottomNavigationAction-root .MuiSvgIcon-root': {
      boxSizing: 'content-box',
      px: 2,
      py: 0.375,
      borderRadius: 4,
      transition: 'background-color 0.2s',
    },
    '& .MuiBottomNavigationAction-root.Mui-selected': { color: 'primary.dark' },
    '& .MuiBottomNavigationAction-root.Mui-selected .MuiSvgIcon-root': { bgcolor: '#EEF5E3' },
    '& .MuiBottomNavigationAction-label.Mui-selected': { fontWeight: 600 },
  } as const;

  const getPageTitle = () => {
    const path = location.pathname;
    switch (path) {
      case '/':
        return t('nav.controlPanel');
      case '/hardware':
        return t('nav.hardware');
      case '/projects':
        return t('nav.projects');
      case '/products':
        return t('nav.mixDesigns');
      case '/suppliers':
      case '/batching-plants':
        return t('nav.batchPlants');
      case '/users':
        return t('nav.users');
      case '/companies':
        return t('nav.clients');
      case '/maturity-inputs':
        return [
          t('nav.maturityInputs'),
          ...sectionCrumbs(sectionFromSearch(new URLSearchParams(location.search)), t),
        ].join(' > ');
      case '/settings/client-details':
        return `${t('nav.settingsGroup')} > ${t('settings.clientDetails', 'Client Details')}`;
      case '/settings/general':
        return `${t('nav.settingsGroup')} > ${t('settings.generalSettings', 'General Settings')}`;
      case '/settings/units':
        return `${t('nav.settingsGroup')} > ${t('settings.unitsOfMeasure', 'Units of Measure')}`;
      case '/settings/users':
        return `${t('nav.settingsGroup')} > ${t('settings.users', 'Users')}`;
      case '/reports':
        return t('nav.bulkLoggerImport');
      case '/mass-concrete-limits':
        return t('nav.temperatureLimits');
      case '/evaporation-limits':
        return t('nav.evaporationLimits');
      case '/environmental-limits':
        return t('nav.environmentalLimits');
      case '/help':
        return t('nav.trainingHub');
      case '/pours':
        return t('nav.controlPanel');
      default:
        if (path.startsWith('/users/new')) {
          return t('pageTitles.createNewUser');
        } else if (path.startsWith('/users/') && path.includes('/edit')) {
          return t('pageTitles.editUser');
        } else if (path.startsWith('/users/') && path.includes('/change-password')) {
          return t('pageTitles.changePassword', 'User Details > Change Password');
        } else if (path.startsWith('/users/') && path !== '/users') {
          return t('pageTitles.userDetails');
        } else if (path.startsWith('/companies/new')) {
          return t('pageTitles.createNewCompany');
        } else if (path.startsWith('/companies/') && path.includes('/edit')) {
          return t('pageTitles.editCompany');
        } else if (path.startsWith('/companies/') && path.includes('/users')) {
          return t('pageTitles.companyUsers');
        } else if (path.startsWith('/companies/') && path !== '/companies') {
          return t('pageTitles.companyDetails');
        } else if (path.startsWith('/hardware/')) {
          return t('nav.hardware');
        } else if (path === '/pours/new') {
          return t('pageTitles.createPour', 'Create a New Pour');
        } else if (path.endsWith('/dashboard') && path.startsWith('/pours/')) {
          // Published by the dashboard page; rendered as a breadcrumb by renderPageTitle.
          return [pourTitle?.project, pourTitle?.pour].filter(Boolean).join(' > ');
        } else if (path.endsWith('/edit') && path.startsWith('/pours/')) {
          return t('pageTitles.editPour', 'Edit Pour');
        } else if (path.startsWith('/pours/')) {
          return t('pageTitles.pourDetails', 'Pour Details');
        } else if (path.startsWith('/products/new')) {
          return t('pageTitles.addNewProduct');
        } else if (path.startsWith('/products/') && path.includes('/edit')) {
          return t('pageTitles.editProduct');
        } else if (path.startsWith('/products/') && path !== '/products') {
          return t('pageTitles.productDetails');
        } else if (path.startsWith('/projects/new')) {
          return t('pageTitles.createNewProject');
        } else if (path.startsWith('/projects/') && path.includes('/edit')) {
          return t('pageTitles.editProject');
        } else if (path.startsWith('/projects/') && path !== '/projects') {
          return t('pageTitles.projectDetails');
        } else if (path.startsWith('/calibrations/new')) {
          return t('pageTitles.newCalibration');
        } else if (path.startsWith('/calibrations/')) {
          return t('pageTitles.calibrationDetails');
        } else if (path.startsWith('/mass-concrete-limits')) {
          return t('nav.temperatureLimits');
        } else if (path.startsWith('/evaporation-limits')) {
          return t('nav.evaporationLimits');
        } else if (path === '/batching-plants/new') {
          return t('pageTitles.createBatchPlant', 'Create new Batch Plant');
        } else if (path.startsWith('/batching-plants/') && path.endsWith('/edit')) {
          return t('pageTitles.editBatchPlant', 'Edit Batch Plant');
        } else if (path.startsWith('/batching-plants/') && path !== '/batching-plants') {
          return t('pageTitles.batchPlantSummary', 'Batch Plant Summary');
        } else if (path.startsWith('/batching-plants')) {
          return t('nav.batchPlants');
        }
        return 'ConXEdge';
    }
  };

  // Page title as rendered in the header. The pour dashboard shows a
  // breadcrumb: project name in a lighter weight, then the pour name in bold.
  const renderPageTitle = () => {
    if (isPourDashboard && pourTitle && (pourTitle.project || pourTitle.pour)) {
      return (
        <>
          {pourTitle.project && (
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {pourTitle.project}
            </Box>
          )}
          {pourTitle.project && pourTitle.pour && (
            <Box component="span" sx={{ mx: 1.25, color: 'grey.400', fontWeight: 300 }}>
              /
            </Box>
          )}
          {pourTitle.pour && <Box component="span">{pourTitle.pour}</Box>}
        </>
      );
    }
    return getPageTitle();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Overlay sidebar: the page is always laid out for the slim rail, and the
  // expanded drawer floats over it — content never resizes, so there is
  // nothing to reflow or crossfade when toggling.
  const handleSidebarToggle = () => {
    setFlyout(null);
    setSidebarOpen(!sidebarOpen);
  };

  // Collapsed-rail flyout menus: hovering (or clicking) a group icon pops its
  // children out beside the rail, so grouped pages are one click away without
  // expanding the sidebar. A short close delay lets the pointer cross the gap
  // between rail and panel.
  const [flyout, setFlyout] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const flyoutCloseTimer = React.useRef<number | undefined>(undefined);
  const openFlyout = (id: string, anchor: HTMLElement) => {
    window.clearTimeout(flyoutCloseTimer.current);
    setFlyout({ id, anchor });
  };
  const scheduleFlyoutClose = () => {
    window.clearTimeout(flyoutCloseTimer.current);
    flyoutCloseTimer.current = window.setTimeout(() => setFlyout(null), 150);
  };
  const cancelFlyoutClose = () => window.clearTimeout(flyoutCloseTimer.current);
  React.useEffect(() => () => window.clearTimeout(flyoutCloseTimer.current), []);

  // Notifications panel: the bell row (desktop) or drawer bell (phone)
  // that opened it. The phone drawer closes so the full-screen panel is
  // what the user sees.
  const [notificationsAnchor, setNotificationsAnchorState] = useState<HTMLElement | null>(null);
  const setNotificationsAnchor = (el: HTMLElement | null) => {
    if (el) setMobileOpen(false);
    setNotificationsAnchorState(el);
  };
  const { unreadCount: unreadNotifications } = useNotifications();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const visibleKeys = getVisibleMenuKeys(user);

  // Order and grouping follow the UI Development doc (27.07.2026).
  const allNav: NavEntry[] = [
    leaf('controlPanel', t('nav.controlPanel'), <ControlPanelIcon />, '/'),
    leaf('hardware', t('nav.hardware'), <HardwareNodesIcon />, '/hardware'),
    leaf('projects', t('nav.projects'), <ProjectsBuildingsIcon />, '/projects'),
    // Batching Plants is a top-level item (Figma sidebar, Sept 2026); it
    // previously sat under Limits & Targets.
    leaf('suppliers', t('nav.batchPlants'), <BatchingPlantIcon />, '/batching-plants'),
    leaf('products', t('nav.mixDesigns'), <MixerTruckIcon />, '/products'),
    leaf('users', t('nav.users'), <UsersGroupIcon />, '/users'),
    leaf('companies', t('nav.clients'), <ClientsCardIcon />, '/companies'),
    {
      kind: 'group',
      id: 'limitsTargets',
      text: t('nav.limitsTargets'),
      icon: <LimitsChartIcon />,
      children: [
        leaf('temperatureLimits', t('nav.temperatureLimits'), <TargetsIcon />, '/mass-concrete-limits'),
        leaf('maturityInputs', t('nav.maturityInputs'), <MaturityIcon />, '/maturity-inputs'),
        // No Concrete Strength Targets item: targets are set on each mix
        // design (its target groups); /maturity-targets redirects there.
        leaf('evaporationLimit', t('nav.evaporationLimits'), <EvaporationIcon />, '/evaporation-limits'),
        leaf('environmentalLimits', t('nav.environmentalLimits'), <EnvironmentIcon />, '/environmental-limits'),
      ],
    },
    // Settings section (replaces the old Company Details summary page and the
    // Units of Measure placeholder): sub-navigation lives inside the page.
    leaf('settings', t('nav.settingsGroup'), <SettingsCogIcon />, '/settings'),
    leaf('help', t('nav.trainingHub'), <TrainingCapIcon />, '/help'),
    // Super-admin only from here down (gated by menu keys)
    leaf('reports', t('nav.bulkLoggerImport'), <BulkImportIcon />, '/reports'),
    // Administration group — hides itself when no child is visible. Future
    // admin-settings pages slot in here alongside the simulator.
    {
      kind: 'group',
      id: 'administration',
      text: t('nav.administration', 'Administration'),
      icon: <AdminSlidersIcon />,
      children: [
        leaf('simulator', t('nav.simulator'), <SimulatorIcon />, '/admin/simulator'),
      ],
    },
  ];

  // Keep only what the user may see: visible leaves, and groups with ≥1 visible child.
  const navEntries: NavEntry[] = allNav
    .map((entry): NavEntry | null => {
      if (entry.kind === 'leaf') return visibleKeys.has(entry.key) ? entry : null;
      const children = entry.children.filter((c) => visibleKeys.has(c.key));
      return children.length ? { ...entry, children } : null;
    })
    .filter((e): e is NavEntry => e !== null);

  // Active-item matching: longest matching prefix wins, so detail pages
  // (/projects/123, /mass-concrete-limits/new, /pours/{id}/dashboard) keep their
  // parent menu item lit. Control Panel lives at '/' but owns the /pours subtree.
  const matchesPath = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/pours' || location.pathname.startsWith('/pours/');
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  const activePath = navEntries
    .flatMap((e) => (e.kind === 'leaf' ? [e] : e.children))
    .filter((l) => matchesPath(l.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  const drawerContent = (isOpen: boolean, isMobile: boolean = false) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Brand header — full wordmark when open, round X mark when collapsed.
          The sidebar now runs to the top of the viewport, so the logo lives here.
          The phone drawer shows the wordmark (tap: Control Panel) and, under
          it, the company's own logo or name (the mobile AppBar has no logos). */}
      {isMobile ? (
        <Box sx={{ flexShrink: 0, px: 1, pt: 1, pb: 1.5 }}>
          <ButtonBase
            onClick={() => { navigate('/'); setMobileOpen(false); }}
            sx={{ minHeight: 48, px: 1, borderRadius: 1, justifyContent: 'flex-start' }}
          >
            <Box
              component="img"
              src="/Conxedge-black.png"
              alt="ConXEdge"
              sx={{ height: 30, maxWidth: 140, objectFit: 'contain' }}
            />
          </ButtonBase>
          <Box sx={{ px: 1, mt: 0.5, minWidth: 0 }}>
            <CompanyBrand variant="drawer" />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            minHeight: 64,
            flexShrink: 0,
          }}
        >
          <>
            {/* The round X mark stays put at the same size in both states —
                centred on the icon column like the menu icons below, so
                expanding the drawer never moves or rescales it. Only the
                "conXedge" lettering (the wordmark image with its own leading X
                cropped off) fades in beside it. Both toggle the sidebar. */}
            <Tooltip
              title={isOpen ? t('nav.collapseMenu', 'Collapse menu') : t('nav.expandMenu', 'Expand menu')}
              placement="right"
              arrow
            >
              <IconButton
                onClick={handleSidebarToggle}
                sx={{ position: 'absolute', left: 15, p: 0.5 }}
              >
                <Box
                  component="img"
                  src="/conxedge-x-black.png"
                  alt="ConXEdge"
                  sx={{ width: 34, height: 34 }}
                />
              </IconButton>
            </Tooltip>
            <Box
              onClick={handleSidebarToggle}
              sx={{
                position: 'absolute',
                left: 54,
                height: 28,
                width: 126,
                overflow: 'hidden',
                cursor: 'pointer',
                opacity: isOpen ? 1 : 0,
                pointerEvents: isOpen ? 'auto' : 'none',
                transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {/* -27px hides the wordmark's own 44px-wide X badge (scaled to
                  this 28px render height) so only the lettering shows. */}
              <Box
                component="img"
                src="/Conxedge-black.png"
                alt=""
                sx={{ height: 28, ml: '-27px' }}
              />
            </Box>
            <IconButton
              onClick={handleSidebarToggle}
              size="small"
              sx={{
                position: 'absolute',
                right: 2,
                color: 'rgba(255,255,255,0.7)',
                opacity: isOpen ? 1 : 0,
                pointerEvents: isOpen ? 'auto' : 'none',
                visibility: isOpen ? 'visible' : 'hidden',
                transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.2s',
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </>
        </Box>
      )}

      {/* Navigation */}
      <List
        sx={{
          px: 1,
          pt: 1,
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {(() => {
          // Doc spec: icon in a circle; active page's circle gets a green outer
          // glow, the rest a grey one. Kept deliberately soft — on the black
          // sidebar even low-alpha halos read clearly, and anything stronger
          // turns the column into a wall of bubbles.
          const iconCircle = (icon: React.ReactNode, active: boolean) => (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: active ? 'rgba(154,208,46,0.12)' : 'rgba(255,255,255,0.05)',
                boxShadow: active
                  ? '0 0 12px 2px rgba(154,208,46,0.55)'
                  : '0 0 5px rgba(255,255,255,0.14)',
                transition: 'box-shadow 0.2s, background-color 0.2s',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          );

          // A single navigable link — used for both top-level items and the
          // indented children inside a collapsible group.
          const renderLeaf = (item: NavLeaf, indented: boolean) => {
            const isSelected = item.path === activePath;
            const button = (
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                  else setSidebarOpen(false);
                }}
                selected={isSelected}
                sx={{
                  borderRadius: '6px',
                  color: 'rgba(255,255,255,0.7)',
                  // 48px touch rows in the phone drawer
                  minHeight: isMobile ? 48 : 40,
                  py: 0.5,
                  justifyContent: 'initial',
                  px: 1.5,
                  // Left padding stays constant between open/collapsed so the
                  // icon column never moves; indented rows slide back as their
                  // group collapses away.
                  pl: isOpen && indented
                    ? (classicSidebar ? 3 : 2.5)
                    : (classicSidebar ? 1.5 : 1),
                  transition: 'padding 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: 'common.white',
                  },
                  // Selected state: classic = highlighted row with a green
                  // left border; new look = the icon circle's glow carries it.
                  '&.Mui-selected': classicSidebar
                    ? {
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        color: 'common.white',
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                        borderRadius: '0 6px 6px 0',
                        // 3px border eats into the padding — compensate so the
                        // icon lines up with unselected rows in both states.
                        pl: isOpen && indented ? 2.75 : 1.125,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.18)',
                        },
                      }
                    : {
                        backgroundColor: 'transparent',
                        color: 'common.white',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.08)',
                        },
                      },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: classicSidebar ? 'inherit' : (isSelected ? 'primary.light' : 'inherit'),
                    minWidth: classicSidebar ? 32 : 40,
                    justifyContent: 'center',
                  }}
                >
                  {classicSidebar ? item.icon : iconCircle(item.icon, isSelected)}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.82rem',
                    noWrap: true,
                    // Native tooltip reveals labels that ellipsize at this width
                    title: item.text,
                  }}
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    opacity: isOpen ? 1 : 0,
                    transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </ListItemButton>
            );

            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                {isOpen ? button : (
                  <Tooltip title={item.text} placement="right" arrow>
                    {button}
                  </Tooltip>
                )}
              </ListItem>
            );
          };

          return navEntries.map((entry) => {
            if (entry.kind === 'leaf') return renderLeaf(entry, false);

            // Collapsible group. When the sidebar is collapsed to icons there's no
            // room to expand inline, so the icon jumps to the first child instead.
            const hasActiveChild = entry.children.some((c) => c.path === activePath);
            const expanded = openGroups[entry.id] ?? hasActiveChild;

            const header = (
              <ListItemButton
                onClick={(e) => {
                  if (!isOpen) {
                    // Collapsed rail: the flyout does the navigating.
                    openFlyout(entry.id, e.currentTarget);
                  } else {
                    setOpenGroups((prev) => ({ ...prev, [entry.id]: !expanded }));
                  }
                }}
                onMouseEnter={!isOpen ? (e) => openFlyout(entry.id, e.currentTarget) : undefined}
                onMouseLeave={!isOpen ? scheduleFlyoutClose : undefined}
                sx={{
                  borderRadius: '6px',
                  color: hasActiveChild ? 'common.white' : 'rgba(255,255,255,0.7)',
                  minHeight: isMobile ? 48 : 40,
                  py: 0.5,
                  justifyContent: 'initial',
                  px: 1.5,
                  pl: classicSidebar ? 1.5 : 1,
                  transition: 'padding 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: 'common.white',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: classicSidebar ? 'inherit' : (hasActiveChild ? 'primary.light' : 'inherit'),
                    minWidth: classicSidebar ? 32 : 40,
                    justifyContent: 'center',
                  }}
                >
                  {classicSidebar ? entry.icon : iconCircle(entry.icon, hasActiveChild)}
                </ListItemIcon>
                <ListItemText
                  primary={entry.text}
                  primaryTypographyProps={{ fontSize: '0.82rem', noWrap: true, title: entry.text }}
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    opacity: isOpen ? 1 : 0,
                    transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
                {expanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ opacity: isOpen ? 1 : 0, transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ opacity: isOpen ? 1 : 0, transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  />
                )}
              </ListItemButton>
            );

            return (
              <React.Fragment key={entry.id}>
                <ListItem disablePadding sx={{ mb: 0.25 }}>
                  {header}
                </ListItem>
                <Collapse in={isOpen && expanded} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {entry.children.map((child) => renderLeaf(child, true))}
                  </List>
                </Collapse>
                {/* Collapsed-rail flyout: the group's children beside the rail,
                    one click to navigate without expanding the sidebar. */}
                {!isOpen && !isMobile && (
                  <Popper
                    open={flyout?.id === entry.id}
                    anchorEl={flyout?.id === entry.id ? flyout.anchor : null}
                    placement="right-start"
                    sx={{ zIndex: (theme) => theme.zIndex.modal }}
                  >
                    <ClickAwayListener
                      mouseEvent="onMouseDown"
                      touchEvent="onTouchStart"
                      onClickAway={() => setFlyout(null)}
                    >
                      <Paper
                        onMouseEnter={cancelFlyoutClose}
                        onMouseLeave={scheduleFlyoutClose}
                        sx={{
                          ml: 0.75,
                          minWidth: 210,
                          py: 0.5,
                          backgroundColor: 'common.black',
                          color: 'common.white',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.12)',
                          boxShadow: '4px 4px 16px rgba(0,0,0,0.45)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            px: 2,
                            pt: 0.75,
                            pb: 0.5,
                            color: 'rgba(255,255,255,0.5)',
                            fontWeight: 600,
                          }}
                        >
                          {entry.text}
                        </Typography>
                        <List dense disablePadding sx={{ px: 0.5, pb: 0.5 }}>
                          {entry.children.map((child) => {
                            const childActive = child.path === activePath;
                            return (
                              <ListItemButton
                                key={child.path}
                                onClick={() => { navigate(child.path); setFlyout(null); }}
                                selected={childActive}
                                sx={{
                                  borderRadius: '6px',
                                  py: 0.5,
                                  minHeight: 36,
                                  color: childActive ? 'common.white' : 'rgba(255,255,255,0.7)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                    color: 'common.white',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(154,208,46,0.12)',
                                    '&:hover': { backgroundColor: 'rgba(154,208,46,0.18)' },
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 32,
                                    color: childActive ? 'primary.light' : 'rgba(255,255,255,0.7)',
                                  }}
                                >
                                  {child.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.text}
                                  primaryTypographyProps={{ fontSize: '0.82rem', noWrap: true }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                )}
              </React.Fragment>
            );
          });
        })()}
      </List>

      {/* Bottom section */}
      <Box sx={{ mt: 'auto' }}>
        {/* Mini switch: toggle back to the pre-redesign (classic) sidebar look.
            Collapses away smoothly alongside the drawer width animation.
            Desktop only: the phone drawer does not offer it. */}
        {!isMobile && (
          <Collapse in={isOpen} timeout={200}>
            <Box
              sx={{
                px: 2,
                pb: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                opacity: isOpen ? 1 : 0,
                transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' }}
              >
                {t('nav.classicMenu', 'Classic menu')}
              </Typography>
              <Switch
                size="small"
                checked={classicSidebar}
                onChange={(e) => setClassicSidebar(e.target.checked)}
                inputProps={{ 'aria-label': t('nav.classicMenu', 'Classic menu') }}
                sx={{
                  '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              />
            </Box>
          </Collapse>
        )}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

        {user && isMobile ? (
          /* Phone: who is signed in (one line each), the notifications bell
             (full-screen panel) and a 44px Logout. */
          <Box sx={{ pl: 2, pr: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{ color: 'common.white', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}
              >
                {user.UserName || 'User'}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', lineHeight: 1.2 }}
              >
                {user.CompanyName || user.usertype || ''}
              </Typography>
            </Box>
            <IconButton
              onClick={(e) => setNotificationsAnchor(e.currentTarget)}
              aria-label={t('nav.notifications', 'Notifications')}
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'common.white' },
              }}
            >
              <Badge badgeContent={unreadNotifications} color="error">
                <NotificationBellIcon />
              </Badge>
            </IconButton>
            <IconButton
              onClick={handleLogout}
              aria-label={t('userMenu.logout', 'Logout')}
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'common.white' },
              }}
            >
              <ExitToAppIcon />
            </IconButton>
          </Box>
        ) : (
          <>
            {/* Notifications row: opens the application-updates panel */}
            {user && (
              <Tooltip title={isOpen ? '' : t('nav.notifications', 'Notifications')} placement="right" arrow>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={t('nav.notifications', 'Notifications')}
                  onClick={(e) => setNotificationsAnchor(e.currentTarget)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setNotificationsAnchor(e.currentTarget);
                    }
                  }}
                  sx={{
                    px: 2,
                    // Fixed left padding centres the bell on the collapsed
                    // rail's icon column (x=36) so it never moves sideways.
                    pl: '26px',
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'padding 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  <Badge
                    badgeContent={unreadNotifications}
                    color="error"
                    sx={{ '& .MuiBadge-badge': { top: 3, right: 3 } }}
                  >
                    <NotificationBellIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.3rem' }} />
                  </Badge>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.85rem',
                      opacity: isOpen ? 1 : 0,
                      transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {t('nav.notifications', 'Notifications')}
                  </Typography>
                </Box>
              </Tooltip>
            )}

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

            {/* User profile */}
            {user && (
              <Box
                sx={{
                  // 18px left padding centres the 36px avatar in the 72px rail
                  // and lines it up with the icon column, so it never moves.
                  px: 2.25,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  overflow: 'hidden',
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleProfileMenuOpen}
                  sx={{ p: 0 }}
                >
                  <Avatar
                    src={user.ProfileImage ? `${config.apiBaseUrl}/files/user-avatars/${user.ProfileImage}` : undefined}
                    sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.95rem' }}
                  >
                    {user.UserName?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
                <Box
                  sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    opacity: isOpen ? 1 : 0,
                    transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: 'common.white', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}
                    noWrap
                  >
                    {user.UserName || 'User'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', lineHeight: 1.2 }}
                    noWrap
                  >
                    {user.usertype || user.CompanyName || 'User'}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleLogout}
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    '&:hover': { color: 'common.white' },
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                    visibility: isOpen ? 'visible' : 'hidden',
                    transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.2s',
                  }}
                >
                  <ExitToAppIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const drawerPaperStyles = {
    boxSizing: 'border-box',
    width: drawerWidth,
    backgroundColor: 'common.black',
    color: 'common.white',
    borderRight: 'none',
    overflowX: 'hidden' as const,
    transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* Header bar spans only the content area — the sidebar owns the full
          left edge up to the top of the viewport. Below md the sidebar is a
          temporary drawer, so the bar goes full width and shows the page's
          published header (MobileHeaderBar): no logos there. */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          // Fixed to the rail — the overlay drawer slides over it when open.
          width: 'auto',
          // The rail widens by the notch inset on a landscape phone
          // (viewport-fit=cover); the inset is 0 on desktop browsers.
          left: { xs: 0, md: `calc(${drawerWidthClosed}px + env(safe-area-inset-left, 0px))` },
          right: 0,
          pl: { xs: 'env(safe-area-inset-left, 0px)', md: 0 },
          pr: 'env(safe-area-inset-right, 0px)',
          backgroundColor: 'common.white',
          color: '#333333',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar
          ref={mobileBarRef}
          sx={{
            minHeight: { xs: `${MOBILE_APPBAR_HEIGHT}px !important`, md: '64px !important' },
            // Below md the last 48px header button sits as far in from the
            // right edge as the hamburger (edge="start") does from the left.
            pr: { xs: 0.5, sm: 1.5, md: 3 },
          }}
        >
          {isMobileLayout ? (
            <MobileHeaderBar defaultTitle={renderPageTitle()} onOpenMenu={handleDrawerToggle} />
          ) : (
            <>
              <IconButton
                aria-label={t('nav.openDrawer')}
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { md: 'none' }, color: '#333' }}
              >
                <MenuIcon />
              </IconButton>
              {getPageTitle() && (
                <Typography
                  variant="h5"
                  component="h1"
                  noWrap
                  sx={{ fontWeight: 600, fontSize: '1.25rem', color: '#333', display: { xs: 'none', md: 'block' } }}
                >
                  {renderPageTitle()}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {user && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {user.CompanyLogo ? (
                    <img
                      src={`${config.apiBaseUrl}/files/company-logos/${user.CompanyID}/${user.CompanyLogo}`}
                      alt={user.CompanyName || 'Company'}
                      style={{ height: 40, maxWidth: 180, objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ color: '#333', fontWeight: 600, fontSize: '1.1rem' }}>
                      {user.CompanyName || ''}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        id="primary-search-account-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {user?.UserName || 'User'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.CompanyName || 'Company'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate(user?.UserID ? `/users/${user.UserID}/edit` : '/users')}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {t('userMenu.myProfile')}
        </MenuItem>
        {user?.CompanyID && (
          <MenuItem onClick={() => navigate(`/companies/${user.CompanyID}`)}>
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            {t('userMenu.companyInfo')}
          </MenuItem>
        )}
        <Divider />
        <Box sx={{ px: 2, py: 1 }} onClick={(e) => e.stopPropagation()}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {t('userMenu.language', 'Language')}
          </Typography>
          <Select
            value={i18n.language?.split('-')[0] || 'en'}
            onChange={handleLanguageChange}
            size="small"
            fullWidth
            sx={{ fontSize: '0.85rem' }}
          >
            {languages.map((lang) => (
              <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: '0.85rem' }}>
                {lang.nativeName}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Divider />
        {/* Only sign-out on the collapsed rail — the inline logout button next
            to the username is hidden there, so the avatar menu must carry it. */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          {t('userMenu.logout', 'Logout')}
        </MenuItem>
      </Menu>

      <NotificationsPanel
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={() => setNotificationsAnchor(null)}
        fullScreen={isMobileLayout}
      />
      <UpdateAvailableBanner />

      <Box
        component="nav"
        sx={{
          // The layout only ever reserves the slim rail — the expanded drawer
          // floats over the page, so content never moves on toggle. The rail
          // widens by the notch inset on a landscape phone (0 on desktop).
          width: { md: `calc(${drawerWidthClosed}px + env(safe-area-inset-left, 0px))` },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer (below md) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              ...drawerPaperStyles,
              width: 'min(280px, 85vw)',
              // Clear the fixed bottom-nav bar and the iOS home bar so the
              // profile/logout row stays reachable, and a landscape notch.
              pb: aboveBottomBar(0),
              pl: 'env(safe-area-inset-left, 0px)',
            },
          }}
        >
          {drawerContent(true, true)}
        </Drawer>
        {/* Desktop drawer — floats over the page when expanded, so clicking
            anywhere outside retracts it. */}
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={() => { if (sidebarOpen) setSidebarOpen(false); }}
        >
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                ...drawerPaperStyles,
                // A landscape phone at 900px or more (viewport-fit=cover) gets
                // this rail: it widens by the notch inset, 0 on desktop.
                width: `calc(${drawerWidth}px + env(safe-area-inset-left, 0px))`,
                pl: 'env(safe-area-inset-left, 0px)',
                boxShadow: sidebarOpen ? '8px 0 24px rgba(0,0,0,0.35)' : 'none',
              },
            }}
            open
          >
            {drawerContent(sidebarOpen)}
          </Drawer>
        </ClickAwayListener>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          pt: 1.5,
          // Extra bottom space below md so content clears the bottom nav bar
          // (24px above it) and the iOS home bar.
          pb: { xs: aboveBottomBar(24), md: 3 },
          // No width/transition of its own: flexGrow makes it track the nav
          // column's animated width frame-by-frame, so the two can't desync.
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          // The phone Control Panel's and pour dashboard's soft cards sit on a
          // light grey page.
          backgroundColor: { xs: location.pathname === '/' || location.pathname === '/pours' || isPourDashboard ? '#f3f4f6' : 'common.white', md: 'common.white' },
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: `${isMobileLayout ? mobileBarHeight : MOBILE_APPBAR_HEIGHT}px !important`, md: '64px !important' } }} />
        {/* A page that fails to render shows a message with a reload button
            here, and the menu stays usable to move to another page. */}
        <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
      </Box>

      {/* Mobile bottom navigation — primary shortcuts; the hamburger drawer
          still holds the full admin menu. Shown only where the hamburger is
          (below md), so it never collides with the permanent desktop drawer. */}
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          display: { xs: 'block', md: 'none' },
          // The tabs stay clear of the iOS home bar (and a landscape notch).
          pb: SAFE_AREA_BOTTOM,
          pl: 'env(safe-area-inset-left, 0px)',
          pr: 'env(safe-area-inset-right, 0px)',
        }}
      >
        {isPourDashboard && pourNav ? (
          /* On the pour dashboard, the bar becomes the section nav. */
          <BottomNavigation value={pourNav.active} showLabels sx={{ ...bottomNavSx, '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 } }}>
            {pourNav.items.map((item, i) => (
              <BottomNavigationAction
                key={item.key}
                label={item.label}
                value={i}
                icon={item.icon}
                onClick={() => pourNav.onSelect(i)}
              />
            ))}
          </BottomNavigation>
        ) : (
          /* Control Panel, Map, Account. No + New Pour below md: creating a
             pour is desktop work (user decision, 23 Oct 2026). */
          <BottomNavigation value={bottomNavValue} showLabels sx={bottomNavSx}>
            <BottomNavigationAction
              label={t('nav.controlPanel')}
              value="pours"
              icon={<ControlPanelIcon />}
              onClick={() => navigate('/')}
            />
            <BottomNavigationAction
              label={t('nav.map', 'Map')}
              value="map"
              icon={<MapIcon />}
              onClick={() => {
                if (isMapRoute) {
                  // Toggle the map in place on any page that has one
                  mapView.toggle();
                } else {
                  // No map here — fall back to the control panel with map open
                  mapView.setOpen(true);
                  navigate('/');
                }
              }}
            />
            <BottomNavigationAction
              label={t('userMenu.account', 'Account')}
              value="account"
              icon={<PersonIcon />}
              onClick={handleProfileMenuOpen}
            />
          </BottomNavigation>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardLayout;
